import { useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WillEditorContext, burialPlannerHeadings, fieldLabels, viewHeadings } from "../will-editor";
import STKAnimated from "@/components/common/animated";
import { WillFields, WillTypes } from "@/types";
import { PageContentField } from "@/providers/content-provider";

type Props = {
    willType: WillTypes,
    setView: (viewIndex: number) => void
}

const SummarySection = ( props: {heading:string, children:JSX.Element, navigate: () => void} ) => {

        const { css } = useContext(WillEditorContext);

        return(
            <div className={css.summary__section}>
                <h5>{props.heading}</h5>
                <div>
                    {props.children}
                </div>
                <div className={css.summary__editButton}>
                    <button onClick={() => { props.navigate() }}>MUOKKAA <FontAwesomeIcon icon={["fal", "pencil"]} /></button>
                </div>
            </div>
        );
}


const WillEditorSummary = ( props: Props ) => {

    const { will, css, simpleValidator } = useContext(WillEditorContext);
    const { fields } = will;

    const withLabel = ( label:string|undefined, value: string | JSX.Element | undefined, validate:string | undefined = undefined ) => {
        const valid = validate ? simpleValidator.current?.check( value, validate ) : true;
        return (
            <span className={css.summary__withLabel} data-valid={valid}><b>{label}</b>{value}</span>
        );

    } 

    const field = ( field:keyof WillFields, validate:string | undefined = undefined ) => {
        // contactPeople and how are not normal fields
        if ( field !== 'contactPeople' && field !== 'how' ) {
            return withLabel(fieldLabels[field], fields[field], validate);
        } 
        return <></>
    } 

    return(
        <STKAnimated type="down-to-up">
            <PageContentField section={'summary'} index={0} />

            <SummarySection key="summary-section-1" heading={viewHeadings[0]} navigate={ () => { props.setView(0)} }>
                <ul className={css.summary__list}>
                    <li key="summary-section-1__li-1">{field('name', 'required')}</li>
                    <li key="summary-section-1__li-2">{field('ssn', 'required')}</li>
                    <li key="summary-section-1__li-3">{field('city', 'required')}</li>
                    <li key="summary-section-1__li-4">{field('postalCode', 'required')}</li>
                    <li key="summary-section-1__li-5">{field('address', 'required')}</li>
                    <li key="summary-section-1__li-6">{field('email', 'required|email')}</li>
                    <li key="summary-section-1__li-7">{field('phone', 'required|phone2')}</li>
                </ul>
            </SummarySection>

            { props.willType === 'WILL' &&
                <>
                    <SummarySection key="summary-section-2" heading={`1. ${viewHeadings[1]}`} navigate={ () => { props.setView(1)} }>
                        { 
                            fields?.contactPeople?.map( (person:any, index:number ) => {
                                return(
                                    <ul className={css.summary__list + ( index != 0 ? ' mt-6' : '')} key={`person-${index}`}>
                                        <li key="summary-section-2__li-1">{ withLabel(fieldLabels?.contactPeople?.length ? fieldLabels?.contactPeople[0].name : '', person.name, 'required') }</li>
                                        <li key="summary-section-2__li-2">{ withLabel(fieldLabels?.contactPeople?.length ? fieldLabels?.contactPeople[0].email : '', person.email, 'email') }</li>
                                        <li key="summary-section-2__li-3">{ withLabel(fieldLabels?.contactPeople?.length ? fieldLabels?.contactPeople[0].phone : '', person.phone, 'phone2') }</li>
                                    </ul>
                                )
                            })
                        }
                    </SummarySection>

                    <SummarySection key="summary-section-3" heading={`2. ${viewHeadings[2]}`} navigate={ () => { props.setView(2)} }>
                        <>
                            <p><b>{fieldLabels.how?.value}</b></p>
                            <ul className={css.summary__list}>
                                <li>{withLabel(fields.how?.value, fields.how?.subField)}</li>
                            </ul>
                        </>
                    </SummarySection>

                    <SummarySection key="summary-section-4" heading={`${viewHeadings[3]}`} navigate={ () => { props.setView(3)} }>
                        <>
                            <h6>{fieldLabels?.burialInfo__freeText}</h6>
                            <div className={css.summary__textArea}>
                                <textarea rows={15} readOnly value={fields.burialInfo__freeText}></textarea>
                            </div>
                            <h6>Hautajaissuunnitelma</h6>
                            <ul className={css.summary__list}>
                                <li key="summary-section-4__li-1"><h6>{burialPlannerHeadings[0]}</h6>{field('burialInfo__coffin')}</li>
                                <li key="summary-section-4__li-2"><h6>{burialPlannerHeadings[1]}</h6>{field('burialInfo__stone')}</li>
                                <li key="summary-section-4__li-3"><h6>{burialPlannerHeadings[2]}</h6>{field('burialInfo__ceremony__where')}{field('burialInfo__ceremony__how')}</li>
                                <li key="summary-section-4__li-4"><h6>{burialPlannerHeadings[3]}</h6>{field('burialInfo__memorialService__where')}{field('burialInfo__memorialService__how')}</li>
                                <li key="summary-section-4__li-5"><h6>{burialPlannerHeadings[4]}</h6>{field('burialInfo__obituary')}</li>
                                <li key="summary-section-4__li-6"><h6>{burialPlannerHeadings[5]}</h6>{field('burialInfo__otherWishes')}</li>
                            </ul>
                        </>
                    </SummarySection>

                    <SummarySection key="summary-section-5" heading={`3. ${viewHeadings[4]}`} navigate={ () => {  props.setView( 4 )} }>
                        <ul className={css.summary__list}>
                            <li key="summary-section-5__li-1">{field('documents__where')}</li>
                            <li key="summary-section-5__li-2">{field('documents__what')}</li>
                        </ul>
                    </SummarySection>

                    <SummarySection key="summary-section-6" heading={`${viewHeadings[5]}`}  navigate={ () => { props.setView(5)} }>
                        <div className={css.summary__textArea}>
                            <textarea rows={15} readOnly value={fields.otherNotes}></textarea>
                        </div>
                    </SummarySection>
                </>
            }

            { props.willType === 'WILL_REGISTRATION' &&
                <SummarySection key="summary-section-5" heading={`Rekisteröi laatimasi testamentti`} navigate={ () => {  props.setView( 1 )} }>
                    <ul className={css.summary__list}>
                        <li key="summary-section-5__li-1">{field('willRegistration__where')}</li>
                        <li key="summary-section-5__li-2">{field('willRegistration__moreInfo')}</li>
                    </ul>
                </SummarySection>
            }
            

        </STKAnimated>
    );

}

export default WillEditorSummary;